import {
  MenuItem as HeadlessMenuItem,
  Menu,
  MenuButton,
  MenuItems,
} from '@headlessui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegAddressCard } from 'react-icons/fa';
import { GoPeople } from 'react-icons/go';
import { IoIosSearch } from 'react-icons/io';
import { IoBarChartOutline, IoBusinessOutline } from 'react-icons/io5';
import { LuMessagesSquare, LuSettings2 } from 'react-icons/lu';
import {
  MdLogout,
  MdOutlineAccessTime,
  MdOutlineMedicalServices,
} from 'react-icons/md';
import { RiCalendarScheduleLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router';
import { useDebounceCallback } from 'usehooks-ts';
import { formatPartnerRouteName } from '../../../common/formatter';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useUser from '../../../hooks/useUser';
import { setDashboardMobileSideMenuOpen } from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/functions';
import FeedbackButton from '../../layout/FeedbackButton';
import MenuItem from '../../layout/Navbar/MenuItem';
import MenuSubitem from '../../layout/Navbar/MenuSubitem';

const DashboardSideMenu = () => {
  const appVersion = getEnvironmentVariable('VITE_APP_VERSION');
  const appRuntimeEnv = getEnvironmentVariable('REACT_APP_RUNTIME_PROVIDER');
  const { currentUser, logout } = useUser();
  const location = useLocation();
  const { t } = useTranslation();
  const { navigate, toLocalizedRoute, isSameRoute } = useAppNavigation();
  const dispatch = useDispatch();
  const [newMessagesAmount, setNewMessagesAmount] = useState<number>(0);

  const { conversations } = useSelector((state: RootReducerType) => state.chat);

  const onNavigateToRoute = (route: string) => {
    navigate(route);
    dispatch(setDashboardMobileSideMenuOpen(false));
  };

  const onNewTabNavigation = useDebounceCallback((url) => {
    window.open(url);
  }, 100);

  const partnerRoute = useMemo<string>(() => {
    if (currentUser) {
      return toLocalizedRoute(
        ROUTES.PARTNER_PUBLIC_PROFILE_ROUTE.replace(
          ':partnerId',
          `${currentUser?.partner_id}`,
        ).replace(
          ':partnerName',
          formatPartnerRouteName(
            `${currentUser?.first_name} ${currentUser?.last_name}`,
          ),
        ),
      );
    }

    return '/';
  }, [currentUser, toLocalizedRoute]);

  useEffect(() => {
    if (conversations) {
      let msgAmounts = 0;
      for (let index = 0; index < conversations.conversations.length; index++) {
        const conversation = conversations.conversations[index];
        msgAmounts += conversation.data.new_messages;
      }

      if (newMessagesAmount !== msgAmounts) {
        setNewMessagesAmount(msgAmounts);
      }
    }
  }, [conversations]);

  useEffect(() => {}, []);

  return (
    <div className="flex min-h-full min-w-full grow flex-col items-start justify-start">
      <button
        id="dashboard-side-menu-item-appointments"
        key="dashboard-side-menu-item-appointments"
        aria-label="dashboard-side-menu-item-appointments"
        data-test-id="dashboard-side-menu-item-appointments"
        data-umami-event="dashboard-side-menu-item-appointments"
        className={`min-w-full ${
          !isSameRoute(
            location.pathname,
            ROUTES.DASHBOARD_ROUTE_APPOINTMENTS,
          ) && !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)
            ? 'text-white lg:text-slate-700'
            : ''
        }`}
        disabled={
          isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_APPOINTMENTS) ||
          isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)
        }
        onClick={() => onNavigateToRoute(ROUTES.DASHBOARD_ROUTE_APPOINTMENTS)}
        type="button"
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-appointments"
          active={
            !isSameRoute(
              location.pathname,
              ROUTES.DASHBOARD_ROUTE_APPOINTMENTS,
            ) && !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE)
          }
          label={t('static.dashboard.side_menu_item_label_appointments')}
          icon={RiCalendarScheduleLine}
        />
      </button>
      <button
        id="dashboard-side-menu-item-clients"
        key="dashboard-side-menu-item-clients"
        aria-label="dashboard-side-menu-item-clients"
        data-test-id="dashboard-side-menu-item-clients"
        data-umami-event="dashboard-side-menu-item-clients"
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS) &&
          'text-white lg:text-slate-700'
        }`}
        disabled={isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_CONTACTS,
        )}
        onClick={() =>
          onNavigateToRoute(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_CONTACTS))
        }
        type="button"
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-clients"
          active={
            !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_CONTACTS)
          }
          label={t('static.dashboard.side_menu_item_label_clients')}
          icon={GoPeople}
        />
      </button>
      <button
        id="dashboard-side-menu-item-chat"
        key="dashboard-side-menu-item-chat"
        aria-label="dashboard-side-menu-item-chat"
        data-test-id="dashboard-side-menu-item-chat"
        data-umami-event="dashboard-side-menu-item-chat"
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE) &&
          'text-white lg:text-slate-700'
        }`}
        disabled={isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE)}
        onClick={() =>
          onNavigateToRoute(toLocalizedRoute(ROUTES.MESSAGES_ROUTE))
        }
        type="button"
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-chat"
          active={!isSameRoute(location.pathname, ROUTES.MESSAGES_ROUTE)}
          label={t('static.dashboard.side_menu_item_label_chat')}
          icon={LuMessagesSquare}
          notificationsAmount={
            newMessagesAmount > 0 ? newMessagesAmount : undefined
          }
        />
      </button>
      <button
        id="dashboard-side-menu-item-businesses"
        key="dashboard-side-menu-item-businesses"
        aria-label="dashboard-side-menu-item-businesses"
        data-test-id="dashboard-side-menu-item-businesses"
        data-umami-event="dashboard-side-menu-item-businesses"
        onClick={() =>
          onNavigateToRoute(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_BUSINESS))
        }
        type="button"
        disabled={isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_BUSINESS,
        )}
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS) &&
          'text-white lg:text-slate-700'
        }`}
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-businesses"
          active={
            !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_BUSINESS)
          }
          label={t('static.dashboard.side_menu_item_label_businesses')}
          icon={IoBusinessOutline}
        />
      </button>
      <button
        id="dashboard-side-menu-item-services"
        key="dashboard-side-menu-item-services"
        aria-label="dashboard-side-menu-item-services"
        data-test-id="dashboard-side-menu-item-services"
        data-umami-event="dashboard-side-menu-item-services"
        onClick={() =>
          onNavigateToRoute(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_SERVICE))
        }
        type="button"
        disabled={isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_SERVICE,
        )}
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE) &&
          'text-white lg:text-slate-700'
        }`}
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-services"
          active={
            !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_SERVICE)
          }
          label={t('static.dashboard.side_menu_item_label_services')}
          icon={MdOutlineMedicalServices}
        />
      </button>
      <Menu>
        <MenuButton
          data-test-id="dashboard-side-menu-item-hours"
          data-umami-event="dashboard-side-menu-item-hours"
          type="button"
          disabled={false}
          className="min-w-full text-white data-[open]:bg-gray-100 data-[open]:text-slate-700 lg:text-slate-700"
        >
          <MenuItem
            data-test-id="dashboard-side-menu-item-hours"
            active
            label={t('static.dashboard.side_menu_item_label_hours')}
            icon={MdOutlineAccessTime}
            submenus
          />
        </MenuButton>
        <MenuItems
          transition
          className="flex min-w-full flex-col items-center justify-center data-[open]:bg-gray-100"
        >
          <HeadlessMenuItem>
            <button
              id="dashboard-side-menu-item-temp-hours-closing"
              key="dashboard-side-menu-item-temp-hours-closing"
              aria-label="dashboard-side-menu-item-temp-hours-closing"
              data-test-id="dashboard-side-menu-item-temp-hours-closing"
              data-umami-event="dashboard-side-menu-item-temp-hours-closing"
              onClick={() =>
                onNavigateToRoute(
                  toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_TEMP_CLOSING_HOURS),
                )
              }
              type="button"
              className="flex min-w-full text-white lg:text-slate-700"
            >
              <MenuSubitem
                data-test-id="dashboard-side-menu-item-temp-hours-closing"
                active
                label={t(
                  'static.dashboard.side_menu_item_label_hours_temp_hours_closing',
                )}
              />
            </button>
          </HeadlessMenuItem>
        </MenuItems>
      </Menu>
      <button
        id="dashboard-side-menu-item-overview"
        key="dashboard-side-menu-item-overview"
        aria-label="dashboard-side-menu-item-overview"
        data-test-id="dashboard-side-menu-item-overview"
        data-umami-event="dashboard-side-menu-item-overview"
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_OVERVIEW) &&
          'text-white lg:text-slate-700'
        }`}
        disabled={isSameRoute(
          location.pathname,
          ROUTES.DASHBOARD_ROUTE_OVERVIEW,
        )}
        onClick={() =>
          onNavigateToRoute(toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_OVERVIEW))
        }
        type="button"
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-overview"
          active={
            !isSameRoute(location.pathname, ROUTES.DASHBOARD_ROUTE_OVERVIEW)
          }
          label={t('static.dashboard.side_menu_item_label_overview')}
          icon={IoBarChartOutline}
        />
      </button>
      <button
        id="dashboard-side-menu-item-profile"
        key="dashboard-side-menu-item-profile"
        aria-label="dashboard-side-menu-item-profile"
        data-test-id="dashboard-side-menu-item-profile"
        data-umami-event="dashboard-side-menu-item-profile"
        onClick={() => onNewTabNavigation(partnerRoute)}
        type="button"
        disabled={location.pathname === partnerRoute}
        className={`min-w-full ${
          location.pathname !== partnerRoute &&
          'hidden text-white lg:text-slate-700'
        }`}
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-profile"
          active={location.pathname !== partnerRoute}
          label={t('static.dashboard.side_menu_item_label_profile')}
          icon={FaRegAddressCard}
        />
      </button>
      <button
        id="dashboard-side-menu-item-search"
        key="dashboard-side-menu-item-search"
        aria-label="dashboard-side-menu-item-search"
        data-test-id="dashboard-side-menu-item-search"
        data-umami-event="dashboard-side-menu-item-search"
        onClick={() =>
          onNewTabNavigation(toLocalizedRoute(ROUTES.SEARCH_ROUTE))
        }
        type="button"
        disabled={isSameRoute(location.pathname, ROUTES.SEARCH_ROUTE)}
        className={`min-w-full ${
          !isSameRoute(location.pathname, ROUTES.SEARCH_ROUTE) &&
          'text-white lg:text-slate-700'
        } hidden`}
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-search"
          active={!isSameRoute(location.pathname, ROUTES.SEARCH_ROUTE)}
          label={t('static.dashboard.side_menu_item_label_search')}
          icon={IoIosSearch}
        />
      </button>
      <Menu>
        <MenuButton
          data-test-id="dashboard-side-menu-item-preferences"
          data-umami-event="dashboard-side-menu-item-preferences"
          type="button"
          disabled={false}
          className="min-w-full text-white data-[open]:bg-gray-100 data-[open]:text-slate-700 lg:text-slate-700"
        >
          <MenuItem
            data-test-id="dashboard-side-menu-item-preferences"
            active
            label={t('static.dashboard.side_menu_item_label_preferences')}
            icon={LuSettings2}
            submenus
          />
        </MenuButton>
        <MenuItems
          transition
          className="flex min-w-full flex-col items-center justify-center data-[open]:bg-gray-100"
        >
          <HeadlessMenuItem>
            <button
              id="dashboard-side-menu-item-profile"
              key="dashboard-side-menu-item-profile"
              aria-label="dashboard-side-menu-item-profile"
              data-test-id="dashboard-side-menu-item-profile"
              data-umami-event="dashboard-side-menu-item-profile"
              onClick={() =>
                onNavigateToRoute(
                  toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_SETTINGS),
                )
              }
              type="button"
              className={`min-w-full ${
                !isSameRoute(
                  location.pathname,
                  ROUTES.DASHBOARD_ROUTE_SETTINGS,
                ) && 'hidden text-white lg:text-slate-700'
              }`}
            >
              <MenuSubitem
                data-test-id="dashboard-side-menu-item-preferences"
                active
                label={t(
                  'static.dashboard.side_menu_item_label_preferences_cancel_reasons',
                )}
              />
            </button>
          </HeadlessMenuItem>
          <HeadlessMenuItem>
            <button
              id="dashboard-side-menu-item-profile"
              key="dashboard-side-menu-item-profile"
              aria-label="dashboard-side-menu-item-profile"
              data-test-id="dashboard-side-menu-item-profile"
              data-umami-event="dashboard-side-menu-item-profile"
              onClick={() =>
                onNavigateToRoute(
                  toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_SETTINGS),
                )
              }
              type="button"
              className={`min-w-full ${
                !isSameRoute(
                  location.pathname,
                  ROUTES.DASHBOARD_ROUTE_SETTINGS,
                ) && 'hidden text-white lg:text-slate-700'
              }`}
            >
              <MenuSubitem
                data-test-id="dashboard-side-menu-item-preferences"
                active
                label={t(
                  'static.dashboard.side_menu_item_label_preferences_payment_methods',
                )}
              />
            </button>
          </HeadlessMenuItem>
          <HeadlessMenuItem>
            <button
              id="dashboard-side-menu-item-profile"
              key="dashboard-side-menu-item-profile"
              aria-label="dashboard-side-menu-item-profile"
              data-test-id="dashboard-side-menu-item-profile"
              data-umami-event="dashboard-side-menu-item-profile"
              onClick={() =>
                onNavigateToRoute(
                  toLocalizedRoute(ROUTES.DASHBOARD_ROUTE_PROFILE),
                )
              }
              type="button"
              className={`min-w-full ${
                !isSameRoute(location.pathname, partnerRoute) &&
                'text-white lg:text-slate-700'
              }`}
            >
              <MenuSubitem
                data-test-id="dashboard-side-menu-item-preferences"
                active
                label={t(
                  'static.dashboard.side_menu_item_label_preferences_profile',
                )}
              />
            </button>
          </HeadlessMenuItem>
          <HeadlessMenuItem>
            <button
              id="dashboard-side-menu-item-profile"
              key="dashboard-side-menu-item-profile"
              aria-label="dashboard-side-menu-item-profile"
              data-test-id="dashboard-side-menu-item-profile"
              data-umami-event="dashboard-side-menu-item-profile"
              onClick={() =>
                onNavigateToRoute(toLocalizedRoute(ROUTES.ACCOUNT_ROUTE))
              }
              type="button"
              className={`min-w-full ${
                !isSameRoute(location.pathname, ROUTES.ACCOUNT_ROUTE) &&
                'text-white lg:text-slate-700'
              }`}
            >
              <MenuSubitem
                data-test-id="dashboard-side-menu-item-preferences"
                active
                label={t(
                  'static.dashboard.side_menu_item_label_preferences_account',
                )}
              />
            </button>
          </HeadlessMenuItem>
        </MenuItems>
      </Menu>
      <button
        id="dashboard-side-menu-item-logout"
        key="dashboard-side-menu-item-logout"
        aria-label="dashboard-side-menu-item-logout"
        data-test-id="dashboard-side-menu-item-logout"
        data-umami-event="dashboard-side-menu-item-logout"
        onClick={logout}
        type="button"
        disabled={false}
        className="hidden min-w-full text-white lg:text-slate-700"
      >
        <MenuItem
          data-test-id="dashboard-side-menu-item-logout"
          active
          label={t('static.dashboard.side_menu_item_label_logout')}
          icon={MdLogout}
        />
      </button>
      <span className="grow">&nbsp;</span>
      <span className="flex min-w-full flex-col items-center justify-center">
        <FeedbackButton data-test-id="feedback_button" />
      </span>
      <span className="flex min-w-full flex-col items-center justify-center px-4 pb-1 pt-3 text-center text-xs font-semibold text-slate-200 lg:text-slate-400">
        <span className="flex min-w-full flex-col text-center">
          <Link
            data-test-id="terms-conditions-link"
            to={toLocalizedRoute(ROUTES.TERMS_AND_CONDITIONS_ROUTE)}
            target="_blank"
            className="hover:text-sky-600"
          >
            {t('static.footer.terms_of_service')}
          </Link>
          <Link
            data-test-id="privacy-policy-link"
            to={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}
            target="_blank"
            className="hover:text-sky-600"
          >
            {t('static.footer.privacy_policy')}
          </Link>
          <Link
            data-test-id="cookie-policy-link"
            to={toLocalizedRoute(ROUTES.COOKIE_POLICY_ROUTE)}
            target="_blank"
            className="hover:text-sky-600"
          >
            {t('static.footer.cookie_policy')}
          </Link>
        </span>
        <span className="py-2 hidden lg:block">
          v{`${appVersion ?? '0.0.0-unknown'}_${appRuntimeEnv ?? 'lr'}`}
        </span>
      </span>
    </div>
  );
};

export default DashboardSideMenu;
